import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BRAND_COLORS } from "@/lib/menuData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAFF_QUICK_REPLIES = [
  "Ready in 5 min!",
  "On the way!",
  "Sold out — sorry!",
  "Running about 10 min behind, thanks for waiting.",
  "Your order is ready for pickup.",
  "Please stop by the counter.",
];

// Client-side image compression to keep payloads under ~500KB (backend cap ~600KB).
async function compressToDataUrl(file, { maxDim = 1200, quality = 0.72 } = {}) {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale);
  const h = Math.round(bmp.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bmp, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

/**
 * OrderChatPanel — shared chat UI used by customer, kitchen, and admin.
 *
 * Props:
 *  - orderId (string, required)
 *  - viewer ("customer" | "staff") - controls send endpoint + bubble alignment
 *  - senderName (string) - display name shown to the other side
 *  - authHeaders (object, staff only) - Authorization header for the /messages/staff endpoint
 *  - webSocketMessage (object|null, optional) - if the parent has a live WS, pass its latest message here for live updates
 *  - onUnreadChange (fn, optional) - called with the count of unread messages for this viewer (used by kitchen inbox badge)
 *  - autoMarkRead (bool, default true) - mark messages read on mount + on new incoming
 */
export default function OrderChatPanel({
  orderId,
  viewer = "customer",
  senderName = "",
  authHeaders = {},
  webSocketMessage = null,
  onUnreadChange,
  autoMarkRead = true,
}) {
  const [messages, setMessages] = useState([]);
  const [chatOpen, setChatOpen] = useState(true);
  const [orderSummary, setOrderSummary] = useState(null); // { total, items: [{name, quantity}], status }
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const lastWsIdRef = useRef(null);

  const isStaff = viewer === "staff";

  // Fetch a compact order summary once so the header can show items + total
  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/orders/status/${orderId}`);
        if (!cancelled) setOrderSummary(res.data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  // Mark messages as read (best-effort)
  const markRead = useCallback(async () => {
    if (!orderId || !autoMarkRead) return;
    try {
      await axios.post(`${API}/orders/${orderId}/messages/read`, { by: viewer === "staff" ? "staff" : "customer" });
    } catch {
      /* ignore */
    }
  }, [orderId, viewer, autoMarkRead]);

  // Initial fetch + poll every 4s as a fallback for the WS (works even if the customer isn't authenticated)
  useEffect(() => {
    if (!orderId) return undefined;
    let cancelled = false;
    let interval;
    const fetchAll = async () => {
      try {
        const res = await axios.get(`${API}/orders/${orderId}/messages`);
        if (cancelled) return;
        setMessages(res.data.messages || []);
        const open = res.data.chat_open !== false;
        setChatOpen(open);
        // Once the order closes, staff can still see history but new messages won't come; stop polling
        if (!open && interval) {
          clearInterval(interval);
          interval = null;
        }
      } catch {
        /* order might not exist yet */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    interval = setInterval(fetchAll, 4000);
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [orderId]);

  // Live WS: append when the parent forwards a matching event
  useEffect(() => {
    if (!webSocketMessage || webSocketMessage.type !== "new_message") return;
    const msg = webSocketMessage.message;
    if (!msg || msg.order_id !== orderId) return;
    if (lastWsIdRef.current === msg.id) return;
    lastWsIdRef.current = msg.id;
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  }, [webSocketMessage, orderId]);

  // Auto-scroll to bottom + mark read on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    // Report unread count to the parent (only messages from the OTHER side that we haven't read)
    if (onUnreadChange) {
      const otherSender = isStaff ? "customer" : "staff";
      const readKey = isStaff ? "read_by_staff" : "read_by_customer";
      const unread = messages.filter((m) => m.sender === otherSender && !m[readKey]).length;
      onUnreadChange(unread);
    }
    // Mark read shortly after they show
    if (messages.length) markRead();
  }, [messages, isStaff, onUnreadChange, markRead]);

  const handlePickImage = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    try {
      const dataUrl = await compressToDataUrl(file);
      // Reject if still huge after compression (rare)
      if (dataUrl.length > 780_000) {
        toast.error("Image is too large after compression");
        return;
      }
      setImageDataUrl(dataUrl);
    } catch (err) {
      toast.error("Could not process image");
    }
  };

  const handleSend = async () => {
    const t = text.trim();
    if (!t && !imageDataUrl) return;
    await sendText(t, imageDataUrl);
    setText("");
    setImageDataUrl(null);
  };

  const sendText = async (bodyText, image = null) => {
    if (!bodyText && !image) return;
    if (!chatOpen && !isStaff) {
      toast.error("Chat has closed for this order");
      return;
    }
    setSending(true);
    const endpoint = isStaff
      ? `${API}/orders/${orderId}/messages/staff`
      : `${API}/orders/${orderId}/messages/customer`;
    try {
      const res = await axios.post(
        endpoint,
        { text: bodyText || null, image_data_url: image || null, sender_name: senderName || null },
        { headers: isStaff ? authHeaders : {} }
      );
      setMessages((prev) => (prev.some((m) => m.id === res.data.id) ? prev : [...prev, res.data]));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="order-chat-panel">
      {/* Order summary header — Phase 15.4 (items + total for context) */}
      {orderSummary && (
        <div
          className="px-4 py-2.5 border-b bg-white shrink-0"
          data-testid="chat-order-summary"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: BRAND_COLORS.deepTeal }}>
              Order #{orderSummary.id.slice(0, 8)}
            </span>
            <span className="text-xs font-mono font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>
              ${(orderSummary.total ?? 0).toFixed(2)}
            </span>
          </div>
          {(orderSummary.items || []).length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="chat-order-items">
              {orderSummary.items.slice(0, 6).map((it, i) => (
                <span
                  key={`${it.name}-${i}`}
                  className="inline-block text-[10px] px-1.5 py-0.5 rounded-md"
                  style={{ backgroundColor: BRAND_COLORS.tropicalAqua + "22", color: BRAND_COLORS.darkSlate }}
                >
                  {it.quantity > 1 ? `${it.quantity}× ` : ""}{it.name}
                </span>
              ))}
              {orderSummary.items.length > 6 && (
                <span className="text-[10px] px-1.5 py-0.5" style={{ color: BRAND_COLORS.darkSlate + "88" }}>
                  +{orderSummary.items.length - 6} more
                </span>
              )}
            </div>
          )}
        </div>
      )}
      {/* Messages list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-0"
        style={{ backgroundColor: "#F8FAFC" }}
        data-testid="chat-messages"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full text-slate-400 text-sm">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: BRAND_COLORS.tropicalAqua + "22" }}>
              <Send className="h-6 w-6" style={{ color: BRAND_COLORS.tropicalAqua }} />
            </div>
            <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>
              {isStaff ? "No messages yet" : "Chat with the store"}
            </p>
            <p className="text-xs mt-1 max-w-xs" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
              {isStaff
                ? "Customer messages for this order will appear here."
                : "Ask the store a quick question — they'll see it right away."}
            </p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = (isStaff && m.sender === "staff") || (!isStaff && m.sender === "customer");
            return (
              <div
                key={m.id}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
                data-testid={`chat-message-${m.id}`}
              >
                <div
                  className="max-w-[78%] rounded-2xl px-3 py-2 shadow-sm"
                  style={{
                    backgroundColor: mine ? BRAND_COLORS.deepTeal : "white",
                    color: mine ? "white" : BRAND_COLORS.darkSlate,
                    borderTopRightRadius: mine ? 4 : undefined,
                    borderTopLeftRadius: !mine ? 4 : undefined,
                  }}
                >
                  {!mine && (
                    <p
                      className="text-[10px] font-semibold mb-0.5 uppercase tracking-wider"
                      style={{ color: BRAND_COLORS.tropicalAqua }}
                    >
                      {m.sender_name || (m.sender === "staff" ? "Store" : "Customer")}
                    </p>
                  )}
                  {m.image_data_url && (
                    <img
                      src={m.image_data_url}
                      alt="attachment"
                      className="rounded-lg mb-1 max-h-48 object-contain w-full"
                    />
                  )}
                  {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                  <p
                    className="text-[10px] mt-1 opacity-70 text-right"
                    style={{ color: mine ? "white" : BRAND_COLORS.darkSlate }}
                  >
                    {formatTime(m.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      {!chatOpen && !isStaff ? (
        <div className="p-3 text-center text-xs text-slate-500 border-t bg-white">
          This order is closed — chat is no longer available.
        </div>
      ) : (
        <div className="border-t bg-white p-3 space-y-2" data-testid="chat-composer">
          {/* Staff-only: quick-reply chips */}
          {isStaff && (
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="staff-quick-replies">
              {STAFF_QUICK_REPLIES.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  data-testid={`quick-reply-${preset.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => sendText(preset)}
                  disabled={sending}
                  className="shrink-0 text-xs rounded-full px-3 py-1 border transition-colors hover:bg-slate-100 disabled:opacity-50"
                  style={{
                    borderColor: BRAND_COLORS.tropicalAqua + "55",
                    color: BRAND_COLORS.deepTeal,
                    backgroundColor: BRAND_COLORS.tropicalAqua + "11",
                  }}
                >
                  {preset}
                </button>
              ))}
            </div>
          )}

          {imageDataUrl && (
            <div className="relative inline-block">
              <img src={imageDataUrl} alt="preview" className="h-16 rounded-md object-cover" />
              <button
                data-testid="chat-remove-image"
                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                onClick={() => setImageDataUrl(null)}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFile}
              className="hidden"
              data-testid="chat-file-input"
            />
            <Button
              data-testid="chat-attach-btn"
              variant="ghost"
              size="icon"
              type="button"
              className="rounded-full h-9 w-9 shrink-0"
              onClick={handlePickImage}
              disabled={sending}
            >
              <ImageIcon className="h-4 w-4" />
            </Button>
            <Textarea
              data-testid="chat-input"
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 2000))}
              placeholder={isStaff ? "Reply to the customer…" : "Type a message…"}
              rows={1}
              className="resize-none min-h-9 max-h-24 text-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={sending}
            />
            <Button
              data-testid="chat-send-btn"
              type="button"
              className="rounded-full h-9 w-9 shrink-0 p-0"
              style={{ backgroundColor: BRAND_COLORS.sunsetOrange, color: "white" }}
              onClick={handleSend}
              disabled={sending || (!text.trim() && !imageDataUrl)}
              aria-label="Send"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
